/**
 * The Phase 5E operations: engineering knowledge intelligence — the index's
 * status and jobs, searching, asking a question, reading a question and the
 * excerpts behind its citations, a path's relationships and history, and the
 * advisory impact analysis.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type KnowledgeStatus = Schemas['KnowledgeStatus'];
export type KnowledgeIndex = Schemas['KnowledgeIndex'];
export type KnowledgeIndexState = Schemas['KnowledgeIndexState'];
export type KnowledgeJob = Schemas['KnowledgeJob'];
export type KnowledgeSourceKind = Schemas['KnowledgeSourceKind'];
export type KnowledgeHit = Schemas['KnowledgeHit'];
export type KnowledgeSearchResult = Schemas['KnowledgeSearchResult'];
export type KnowledgeQuestion = Schemas['KnowledgeQuestion'];
export type KnowledgeAnswer = Schemas['KnowledgeAnswer'];
export type KnowledgeCitation = Schemas['KnowledgeCitation'];
export type KnowledgeEvidence = Schemas['KnowledgeEvidence'];
export type KnowledgeExcerpt = Schemas['KnowledgeExcerpt'];
export type KnowledgePathRelations = Schemas['KnowledgePathRelations'];
export type KnowledgeHistory = Schemas['KnowledgeHistory'];
export type KnowledgeImpact = Schemas['KnowledgeImpact'];
export type KnowledgeImpactItem = Schemas['KnowledgeImpactItem'];
export type KnowledgeRevisionContext = Schemas['KnowledgeRevisionContext'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });

export function getKnowledgeStatus(scope: RepoScope): Promise<KnowledgeStatus> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge', {
      params: { path: repoPath(scope) },
    }),
  );
}

export function requestKnowledgeIndex(
  scope: RepoScope,
  body: { full?: boolean } = {},
): Promise<KnowledgeJob> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/knowledge/index', {
      params: { path: repoPath(scope) },
      body: { full: body.full ?? false },
    }),
  );
}

export function getKnowledgeJob(scope: RepoScope, jobId: string): Promise<KnowledgeJob> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge/jobs/{job_id}', {
      params: { path: { ...repoPath(scope), job_id: jobId } },
    }),
  );
}

export function searchKnowledge(
  scope: RepoScope,
  q: string,
  options: { kinds?: KnowledgeSourceKind[]; limit?: number } = {},
): Promise<KnowledgeSearchResult> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge/search', {
      params: {
        path: repoPath(scope),
        query: {
          q,
          ...(options.kinds && options.kinds.length > 0 ? { kinds: options.kinds.join(',') } : {}),
          ...(options.limit ? { limit: options.limit } : {}),
        },
      },
    }),
  );
}

export function askKnowledge(
  scope: RepoScope,
  body: { question: string; revision?: string },
): Promise<KnowledgeQuestion> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/knowledge/questions', {
      params: { path: repoPath(scope) },
      body: { question: body.question, ...(body.revision ? { revision: body.revision } : {}) },
    }),
  );
}

export function getKnowledgeQuestion(
  scope: RepoScope,
  questionId: string,
): Promise<KnowledgeQuestion> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge/questions/{question_id}', {
      params: { path: { ...repoPath(scope), question_id: questionId } },
    }),
  );
}

export function getKnowledgeEvidence(
  scope: RepoScope,
  questionId: string,
): Promise<KnowledgeEvidence> {
  return expect(
    http.GET(
      '/organizations/{org_id}/repositories/{repo_id}/knowledge/questions/{question_id}/evidence',
      { params: { path: { ...repoPath(scope), question_id: questionId } } },
    ),
  );
}

export function getKnowledgeRelations(
  scope: RepoScope,
  path: string,
): Promise<KnowledgePathRelations> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge/relations', {
      params: { path: repoPath(scope), query: { path } },
    }),
  );
}

export function getKnowledgeHistory(scope: RepoScope, path: string): Promise<KnowledgeHistory> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/knowledge/history', {
      params: { path: repoPath(scope), query: { path } },
    }),
  );
}

export function analyzeKnowledgeImpact(
  scope: RepoScope,
  paths: string[],
): Promise<KnowledgeImpact> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/knowledge/impact', {
      params: { path: repoPath(scope) },
      body: { paths },
    }),
  );
}
